/**
 * Haelt den Hinweis zu Bestand und Lieferzeit beim Variantenwechsel aktuell.
 *
 * Horizon laedt beim Wechsel zwar den ganzen Abschnitt neu, tauscht im Dokument
 * aber nur die Teile aus, die sich selbst darum kuemmern - die Variantenauswahl,
 * den Preis, den Kaufknopf. Alles andere bleibt stehen. Deshalb holt sich der
 * Hinweis seinen neuen Inhalt selbst aus dem nachgeladenen Markup.
 *
 * Der Weg ist derselbe wie beim Preis: Das Ereignis traegt ein Versprechen, das
 * mit dem geladenen Dokument aufgeloest wird. Darin steht der Hinweis bereits
 * fertig gerechnet - Bestand, Ausverkauft oder Fertigung, je nach Variante.
 */

const EREIGNIS = 'shopify:product:select';
const UEBERBLENDUNG = 100; // je Richtung; zusammen gut 200 ms

/** Die Info-Zeilen "Lieferzeit wird berechnet ..." als Markup - leer, wenn der Artikel nichts zu waehlen hat. @param {Element} el */
function zeigeInfo(el) {
  const eins = el.getAttribute('data-dp-info-1');
  const zwei = el.getAttribute('data-dp-info-2');
  if (!eins || !zwei) return '';
  const huelle = document.createElement('div');
  const oben = document.createElement('span');
  oben.className = 'dp-lieferung__bestand';
  oben.textContent = eins;
  const unten = document.createElement('span');
  unten.className = 'dp-lieferung__zeit';
  unten.textContent = zwei;
  huelle.append(oben, unten);
  return huelle.innerHTML;
}

class DpLieferung extends HTMLElement {
  /**
   * Tauscht den Inhalt weich: die alte Fassung blendet aus, die neue ein. Die Zeile
   * behaelt ihre Hoehe (der Kasten ist nur der Rahmen, Inhalt und Zustand wechseln
   * mitten in der Ueberblendung). Gleicher Inhalt: nichts passiert.
   *
   * @param {string} html @param {string | null} zustand
   */
  tausche(html, zustand) {
    const setze = () => {
      if (this.innerHTML !== html) this.innerHTML = html;
      // Der Zustand steht als Attribut am Element - daran haengt die
      // Hervorhebung des Ausverkauft-Hinweises.
      if (zustand && this.getAttribute('data-dp-zustand') !== zustand) this.setAttribute('data-dp-zustand', zustand);
    };
    /* Derselbe Text (das Laufschrift-Skript legt Huellen um die Zeilen): kein Tausch, keine Ueberblendung. */
    const probe = document.createElement('div');
    probe.innerHTML = html;
    const gleich = probe.textContent === this.textContent;
    if (gleich || this.innerHTML === html) {
      if (!gleich) setze();
      else if (zustand && this.getAttribute('data-dp-zustand') !== zustand) this.setAttribute('data-dp-zustand', zustand);
      return;
    }
    const ruhig = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (ruhig || !this.offsetWidth || typeof this.animate !== 'function') {
      setze();
      return;
    }
    const nummer = (this.dpTausch = (this.dpTausch || 0) + 1);
    // Laeuft schon eine Ueberblendung, beginnt die neue bei der gerade sichtbaren Deckkraft.
    const start = this.dpAnim ? getComputedStyle(this).opacity : '1';
    if (this.dpAnim) this.dpAnim.cancel();
    const aus = this.animate([{ opacity: start }, { opacity: 0 }], { duration: UEBERBLENDUNG, easing: 'ease-out', fill: 'forwards' });
    this.dpAnim = aus;
    aus.finished
      .then(() => {
        if (nummer !== this.dpTausch) return;
        setze();
        const ein = this.animate([{ opacity: 0 }, { opacity: 1 }], { duration: UEBERBLENDUNG, easing: 'ease-in' });
        this.dpAnim = ein;
        aus.cancel();
        return ein.finished.then(() => {
          if (this.dpAnim === ein) this.dpAnim = null;
        });
      })
      .catch(() => {
        /* Abgebrochene Ueberblendung: die naechste setzt den Stand. */
      });
  }

  connectedCallback() {
    this.abschnitt = this.closest('.shopify-section, dialog');
    if (!this.abschnitt) return;

    this.beiWahl = this.beiWahl.bind(this);
    this.abschnitt.addEventListener(EREIGNIS, this.beiWahl);
  }

  disconnectedCallback() {
    if (this.abschnitt && this.beiWahl) {
      this.abschnitt.removeEventListener(EREIGNIS, this.beiWahl);
    }
  }

  /** @param {Event & { promise?: Promise<any> }} ereignis */
  beiWahl(ereignis) {
    const ziel = ereignis.target;
    // Auswahl in einer Produktkarte geht diesen Hinweis nichts an.
    if (!(ziel instanceof Element) || ziel.closest('product-card')) return;
    if (!ereignis.promise) return;

    ereignis.promise
      .then((ergebnis) => {
        const dokument = ergebnis?.detail?.html;
        if (!dokument) return;

        /*
         * Steht noch eine Option offen (dp-wahlwache.js markiert die Auswahl),
         * gibt es keine Variante, deren Bestand oder Lieferzeit gemeint waere -
         * das Theme hat nur eine beliebige passende gerendert. Der Hinweis
         * bleibt dann leer, wie beim ersten Aufruf.
         */
        if (this.abschnitt && this.abschnitt.querySelector('variant-picker[data-dp-wahl-offen]')) {
          /*
           * Statt Lieferzeit steht die Info, dass sie berechnet wird, sobald
           * gewaehlt ist (die Zeilen stehen als Attribute am Element). Hat der
           * Artikel nichts zu waehlen, bleibt der Hinweis leer.
           */
          const info = zeigeInfo(this);
          this.tausche(info, 'leer');
          return;
        }

        const neu = dokument.querySelector('dp-lieferung');
        if (!neu) return;

        this.tausche(neu.innerHTML, neu.getAttribute('data-dp-zustand'));
      })
      .catch(() => {
        /* Abgebrochene Anfragen sind hier ohne Folgen - der alte Stand bleibt. */
      });
  }
}

if (!customElements.get('dp-lieferung')) {
  customElements.define('dp-lieferung', DpLieferung);
}
